import { useEffect, useRef, useState } from "react";
import { loginUserSelector } from "../../recoil/selectors/loginUserSelector";
import { useRecoilValue } from "recoil";
import {
  ResponseGetNoticeResponse,
  RequestCreateNoticeRequest,
  RequestGetNoticeRequest,
  RequestUpdateNoticeRequest,
  RequestDeleteNoticeRequest,
} from "../../services/OpenAPI";
import { useApiGetNotice } from "../api/useApiGetNotice";
import { useApiCreateNotice } from "../api/useApiCreateNotice";
import { useApiUpdateNotice } from "../api/useApiUpdateNotice";
import { useApiDeleteNotice } from "../api/useApiDeleteNotice";

/**
 * Formats a date string to YYYY/MM/DD format
 */
export const formatDisplayDate = (dateString?: string): string => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}/${month}/${day}`;
  } catch (err) {
    return "";
  }
};

/**
 * Checks if a notice is less than 7 days old (NEW)
 */
export const isNoticeNew = (dateString?: string): boolean => {
  if (!dateString) return false;
  try {
    const noticeDate = new Date(dateString).getTime();
    const now = new Date().getTime();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    return now - noticeDate <= sevenDaysMs;
  } catch (err) {
    return false;
  }
};

export const useNotice = () => {
  const inputRef = useRef<HTMLInputElement>(null);

  const loginUser = useRecoilValue(loginUserSelector);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success",
  );

  const [notices, setNotices] = useState<ResponseGetNoticeResponse[]>([]);

  const { noticeList: fetchedNotices, fetchNotices } = useApiGetNotice();
  const { createNotice } = useApiCreateNotice();
  const { updateNotice: apiUpdate } = useApiUpdateNotice();
  const { deleteNotice: apiDelete } = useApiDeleteNotice();

  const handleCloseToast = () => setToastOpen(false);

  const loadNotices = async () => {
    if (!loginUser) return;
    const req: RequestGetNoticeRequest = {
      baseId: loginUser.baseId ?? 0,
      tenantId: loginUser.tenantId ?? 0,
      userId: loginUser.id ?? 0,
      visible: true,
    };
    const res = await fetchNotices(req);
    if (res) {
      setNotices(res);
    }
  };

  useEffect(() => {
    loadNotices();
  }, [loginUser]);

  const addNotice = async (title: string, content: string) => {
    if (!loginUser) return;
    const req: RequestCreateNoticeRequest = {
      baseId: 0, // loginUser.baseId!,
      tenantId: loginUser.tenantId!,
      userId: 0, // loginUser.id!,
      title,
      content,
      displayAt: new Date().toISOString(),
      visible: true,
    };
    const res = await createNotice(req);
    if (res) {
      setNotices(res);
      setToastMessage("お知らせを追加しました");
      setToastSeverity("success");
      setToastOpen(true);
    }
  };

  const updateNotice = async (notice: ResponseGetNoticeResponse) => {
    if (!loginUser || !notice.id) return;
    const req: RequestUpdateNoticeRequest = {
      id: notice.id,
      baseId: loginUser.baseId!,
      tenantId: loginUser.tenantId!,
      userId: loginUser.id!,
      title: notice.title ?? "",
      content: notice.content ?? "",
      displayAt: notice.displayAt ?? new Date().toISOString(),
      visible: notice.visible,
    };
    const res = await apiUpdate(req);
    if (res) {
      setNotices(res);
      setToastMessage("お知らせを更新しました");
      setToastSeverity("success");
      setToastOpen(true);
    }
  };

  const deleteNotice = async (id: number) => {
    if (!loginUser) return;
    const req: RequestDeleteNoticeRequest = {
      baseId: loginUser.baseId!,
      tenantId: loginUser.tenantId!,
      userId: loginUser.id!,
      ids: [id],
    };
    const res = await apiDelete(req);
    if (res) {
      setNotices(res);
      setToastMessage("お知らせを削除しました");
      setToastSeverity("success");
      setToastOpen(true);
    }
  };

  // チェックされたIDの更新ハンドラ

  return {
    inputRef,
    loginUser,
    toastOpen,
    toastMessage,
    toastSeverity,
    handleCloseToast,
    notices,
    addNotice,
    updateNotice,
    deleteNotice,
    loadNotices,
  };
};
